/**
 * The practice team-invite flow's analytics vocabulary: the `source` tokens the
 * two entry points growth owns report against.
 *
 * WHAT IS HERE AND WHAT IS NOT. There is no payload type and no emit seam in this
 * file, and that is the shape rather than an omission. `send_team_invitation` is
 * emitted once per invitee by the app-side mutation delegate
 * (`src/hooks/mutation/useMutateSendBatchTeamInvites.ts`), which owns that
 * reaction and closes over the team name; what crosses from growth is a single
 * discriminating property. So what this door holds is the token vocabulary, and
 * nothing else.
 *
 * The event name is not here either, and no domain may declare it: `source` is a
 * property of an event three surfaces report — the practice-invite modal, the
 * standalone team-invite modal and the onboarding invite step — and growth owns
 * two of them. The third (`team_invite_modal`) is deliberately ABSENT from the
 * token map below rather than copied in: minting another surface's token here
 * would be a second spelling of a row growth does not report.
 *
 * The app-side binding is a `satisfies Record<TeamInviteSourceToken,
 * TeamInviteSource>` map in the delegate, so a token with no member of the app
 * union fails at the binding rather than at the first emit.
 */

/**
 * The two team-invite entry points inside growth's roots.
 *
 * `practiceInviteModal` is the default for `usePracticeInvite`, which is what the
 * practice-invite modal and the email-invite form both take; `onboardingInviteStep`
 * is passed explicitly by onboarding completion.
 */
export const TEAM_INVITE_SOURCE = {
  practiceInviteModal: 'practice_invite_modal',
  onboardingInviteStep: 'onboarding_invite_step',
} as const;

export type TeamInviteSourceToken =
  (typeof TEAM_INVITE_SOURCE)[keyof typeof TEAM_INVITE_SOURCE];
